import { MantineComponent, Table as MantineTable, rem } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { componentWrapper } from "../../utilities";
import styles from "./table.module.scss";
import type {
    CustomTableFactory,
    CustomTableTdFactory,
    CustomTableThFactory,
    IProps,
    ITableSortingProps,
    ITableTdProps,
    ITableThProps,
} from "./table.types";
import { ThemeExtend } from "@mantine/core/lib/core/factory/factory";
import { Group } from "../group";
import { DropdownIcon } from "../dropdown-icon";
import { AutoMenu } from "../auto-menu";
import { Menu } from "../menu";
import { Icon } from "../icon";
import { faAngleDown, faAngleUp, faFilterList } from "@fortawesome/pro-solid-svg-icons";
import { isValidElement, useMemo } from "react";
import { Box } from "../box";
import { Popover } from "../popover";
import { Grid } from "../grid";
import { Text } from "../text";
import { Button } from "../button";
import { NavLink } from "react-router-dom";

/**
 * Table component for displaying tabular data.
 * @param {boolean} props.hasHorizontalScroll - Whether the table should have horizontal scrolling.
 * @returns {JSX.Element} The Table component.
 */
const Table = componentWrapper<CustomTableFactory, IProps>("Table", ({ hasHorizontalScroll, ...otherProps }, ref) => {
    const TableComponent = useMemo(() => {
        return (
            <MantineTable
                ref={ref}
                verticalSpacing="md"
                horizontalSpacing="md"
                data-has-horizontal-scroll={hasHorizontalScroll}
                classNames={{ table: styles["table"], thead: styles["thead"], th: styles["th"], tr: styles["tr"] }}
                {...otherProps}
            />
        );
    }, [ref, hasHorizontalScroll, otherProps]);
    return TableComponent;
});

/**
 * Table descending sorting menu item.
 * @param {boolean} props.isActive - Whether descending sorting is currently active.
 * @param {boolean} props.disabled - Whether the sorting option is disabled.
 * @param {function} props.onClick - Callback function when descending sorting is selected.
 * @returns {JSX.Element} The TableDescendingSorting component.
 */
const TableDescendingSorting = ({ isActive, disabled, onClick }: ITableSortingProps) => (
    <Menu.Item
        leftSection={<Icon icon={faAngleDown} />}
        active={isActive}
        disabled={disabled}
        value="desc"
        onClick={onClick}
        data-testid="table-sorting-descending"
        miw={rem(180)}
    >
        Descending
    </Menu.Item>
);

/**
 * Table ascending sorting menu item.
 * @param {boolean} props.isActive - Whether ascending sorting is currently active.
 * @param {boolean} props.disabled - Whether the sorting option is disabled.
 * @param {function} props.onClick - Callback function when ascending sorting is selected.
 * @returns {JSX.Element} The TableAscendingSorting component.
 */
const TableAscendingSorting = ({ isActive, disabled, onClick }: ITableSortingProps) => (
    <Menu.Item
        leftSection={<Icon icon={faAngleUp} />}
        active={isActive}
        disabled={disabled}
        value="asc"
        onClick={onClick}
        data-testid="table-sorting-ascending"
        miw={rem(180)}
    >
        Ascending
    </Menu.Item>
);

/**
 * Table header cell component with sorting and filtering support.
 * @param {boolean} props.sortable - Whether the column is sortable.
 * @param {string} props.isSorted - The current sort direction ("asc", "desc", or undefined).
 * @param {boolean} props.filterable - Whether the column is filterable.
 * @param {boolean} props.hasCustomMenu - Whether the column uses a custom menu.
 * @param {object} props.customMenuPopoverProps - Props for the custom menu popover.
 * @param {boolean} props.isFiltered - Whether the column is currently filtered.
 * @param {React.ReactNode} props.children - The header content.
 * @param {Array | React.ReactNode | function} props.menu - Menu items or custom menu renderer.
 * @param {object} props.autoMenuProps - Props for the AutoMenu component.
 * @param {React.ReactNode} props.customTarget - Custom target element for the menu.
 * @param {function} props.onSort - Callback function when sorting is triggered.
 * @returns {JSX.Element} The TableTh component.
 */
const TableTh = componentWrapper<CustomTableThFactory, ITableThProps>(
    "Table",
    (
        {
            sortable,
            isSorted,
            filterable,
            hasCustomMenu,
            customMenuPopoverProps,
            isFiltered,
            children,
            menu,
            autoMenuProps,
            customTarget,
            onSort,
            ...otherProps
        },
        ref
    ) => {
        const [opened, { open, close, toggle }] = useDisclosure();
        const target = useMemo(() => {
            return sortable || filterable || hasCustomMenu || menu ? (
                <Group
                    gap="xs"
                    w="fit-content"
                    wrap="nowrap"
                    align="center"
                    className={styles["th-content"]}
                    py="xs"
                    px="sm"
                    bdrs="sm2"
                    onClick={hasCustomMenu ? toggle : undefined}
                    data-testid="table-header-target"
                >
                    {isFiltered && <Icon size="xxs" icon={faFilterList} />}
                    {children}
                    {(sortable || filterable || hasCustomMenu || menu) && !customTarget && (
                        <DropdownIcon isOpen={isSorted === "asc"} />
                    )}
                    {isValidElement(customTarget) && customTarget}
                </Group>
            ) : (
                <Box data-testid="table-header-target">{children}</Box>
            );
        }, [sortable, filterable, hasCustomMenu, menu, toggle, isFiltered, children, customTarget, isSorted]);

        const menuToRender = useMemo(() => {
            if (hasCustomMenu) {
                if (isValidElement(menu) || typeof menu === "function") {
                    return (
                        <Popover
                            opened={opened}
                            onOpen={open}
                            onClose={close}
                            onDismiss={close}
                            shadow="md"
                            {...customMenuPopoverProps}
                        >
                            <Popover.Target data-testid="table-header-target">{target}</Popover.Target>
                            <Popover.Dropdown>{typeof menu === "function" ? menu(close) : menu}</Popover.Dropdown>
                        </Popover>
                    );
                } else {
                    throw new Error("The menu prop should be a ReactNode when hasCustomMenu is true");
                }
            }
            if (menu) {
                if (typeof menu === "function") {
                    throw new Error("The menu prop can only be a function when used with hasCustomMenu");
                }
                if (Array.isArray(menu)) {
                    return (
                        <AutoMenu
                            {...autoMenuProps}
                            items={[
                                ...(sortable
                                    ? [
                                          <TableAscendingSorting
                                              onClick={typeof onSort !== "undefined" ? () => onSort("asc") : undefined}
                                              key="asc"
                                              isActive={isSorted === "asc"}
                                          />,
                                          <TableDescendingSorting
                                              onClick={typeof onSort !== "undefined" ? () => onSort("desc") : undefined}
                                              key="desc"
                                              isActive={isSorted === "desc"}
                                          />,
                                          ...(menu.length > 0 ? [<Menu.Divider key="divider" />] : []),
                                      ]
                                    : []),
                                ...menu,
                            ]}
                        >
                            {target}
                        </AutoMenu>
                    );
                }
                return (
                    <Menu>
                        <Menu.Target data-testid="table-header-target">{target}</Menu.Target>
                        <Menu.Dropdown>{menu}</Menu.Dropdown>
                    </Menu>
                );
            } else {
                if (sortable) {
                    return (
                        <AutoMenu
                            items={[
                                <TableAscendingSorting
                                    onClick={typeof onSort !== "undefined" ? () => onSort("asc") : undefined}
                                    key="asc"
                                    isActive={isSorted === "asc"}
                                />,
                                <TableDescendingSorting
                                    onClick={typeof onSort !== "undefined" ? () => onSort("desc") : undefined}
                                    key="desc"
                                    isActive={isSorted === "desc"}
                                />,
                            ]}
                        >
                            {target}
                        </AutoMenu>
                    );
                }
            }
            return target;
        }, [
            menu,
            target,
            isSorted,
            sortable,
            hasCustomMenu,
            close,
            customMenuPopoverProps,
            opened,
            open,
            onSort,
            autoMenuProps,
        ]);

        return (
            <MantineTable.Th
                data-sortable={sortable}
                data-filterable={filterable}
                data-is-sorted={isSorted}
                data-is-filtered={isFiltered}
                data-has-custom-menu={hasCustomMenu}
                data-has-menu={!!menu}
                ref={ref}
                classNames={{ th: styles["th"] }}
                {...otherProps}
            >
                {menuToRender}
            </MantineTable.Th>
        );
    }
) as MantineComponent<CustomTableThFactory> & {
    extend: ThemeExtend<CustomTableThFactory>["extend"];
};

TableTh.extend = MantineTable.Th.extend;

/**
 * Table data cell component with optional link support.
 * @param {React.ReactNode} props.children - The cell content.
 * @param {string} props.href - Optional URL to navigate to when the cell is clicked.
 * @returns {JSX.Element} The TableTd component.
 */
const TableTd = componentWrapper<CustomTableTdFactory, ITableTdProps>(
    "Table",
    ({ children, href, ...otherProps }, ref) => {
        return (
            <MantineTable.Td
                ref={ref}
                data-hasLink={typeof href === "string" ? true : href === null ? false : undefined}
                classNames={{ td: styles["td"] }}
                {...otherProps}
            >
                {typeof href === "string" ? (
                    <NavLink data-link to={href}>
                        {children}
                    </NavLink>
                ) : (
                    children
                )}
            </MantineTable.Td>
        );
    }
) as MantineComponent<CustomTableTdFactory> & {
    extend: ThemeExtend<CustomTableTdFactory>["extend"];
};

interface ITablePaginationProps {
    count: number;
    totalCount?: number;
    pagination?: {
        hasNextPage: boolean;
        onNextPage: () => void;
        isLoading: boolean;
    };
}

/**
 * Table pagination component for displaying pagination controls and result counts.
 * @param {number} props.count - The current number of displayed results.
 * @param {number} props.totalCount - The total number of results available.
 * @param {object} props.pagination - Pagination configuration with next page handling.
 * @returns {JSX.Element} The TablePagination component.
 */
const TablePagination = ({ count, totalCount, pagination }: ITablePaginationProps) => {
    return (
        <Grid pt="md" align="center">
            <Grid.Col span={4}>
                <Text dimmed size="sm">
                    {count > 0
                        ? `Showing ${count.toLocaleString()}${!!totalCount ? ` of ${totalCount.toLocaleString()}` : ""} results`
                        : "No results"}
                </Text>
            </Grid.Col>
            <Grid.Col span={4}>
                {pagination && pagination.hasNextPage && (
                    <Group justify="center">
                        <Button
                            variant="white"
                            size="sm"
                            onClick={pagination.onNextPage}
                            loading={pagination.isLoading}
                            data-testid="see-more-button"
                        >
                            See More
                        </Button>
                    </Group>
                )}
            </Grid.Col>
        </Grid>
    );
};

TableTd.extend = MantineTable.Td.extend;

Table.Tr = MantineTable.Tr;
Table.Td = TableTd;
Table.Th = TableTh;
Table.Thead = MantineTable.Thead;
Table.Tbody = MantineTable.Tbody;
Table.Tfoot = MantineTable.Tfoot;
Table.Caption = MantineTable.Caption;
Table.ScrollContainer = MantineTable.ScrollContainer;
Table.DataRenderer = MantineTable.DataRenderer;
Table.Sorting = {
    Ascending: TableAscendingSorting,
    Descending: TableDescendingSorting,
};
Table.Pagination = TablePagination;

export { Table, TableTh, TableTd, TableDescendingSorting, TableAscendingSorting, TablePagination };
